import type { ChangeEvent } from 'react'
import type { SortCriteria } from '@/contexts/products/ProductsContext'
import { VpIcon, VpLink, VpRadioGroup, VpRadioGroupItem, VpRadioGroupItemLabel } from '@vtmn-play/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { InfoTooltip } from '@/components/ProductsGallery/InfoTooltip'
import { ANALYTICS_EVENTS, trackAnalyticsEvent } from '@/services/analytics'
import './sort-dropdown.scss'

interface SortDropdownProps {
  criteria: SortCriteria[]
  disabledCriteria?: SortCriteria[]
  selectedCriteria: SortCriteria
  onSelect: (criteria: SortCriteria) => void
}

export function SortDropdown({
  criteria,
  disabledCriteria = [],
  selectedCriteria,
  onSelect,
}: Readonly<SortDropdownProps>) {
  const { t } = useTranslation()
  const { season } = useParams<{ season: string }>()
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen)
      return

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current
        && !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen])

  const handleSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const c = event.target.value as SortCriteria
    if (disabledCriteria.includes(c))
      return
    trackAnalyticsEvent(ANALYTICS_EVENTS.PRODUCTS_SORT_CHANGED, {
      season,
      sort_criteria: c,
    })
    onSelect(c)
    setIsOpen(false)
  }

  return (
    <div ref={containerRef} className="sort-dropdown">
      <VpLink
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        size="small"
        className="sort-dropdown__trigger"
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span className="sort-dropdown__trigger-text">
          {t('products-page.sort.label')}
          {' '}
          {t(`products-page.sort.criteria.${selectedCriteria}`)}
        </span>
        <VpIcon
          name="chevron-down"
          className={`sort-dropdown__chevron${isOpen ? ' sort-dropdown__chevron--open' : ''}`}
        />
      </VpLink>

      {isOpen && (
        <VpRadioGroup className="sort-dropdown__listbox" value={selectedCriteria} onChange={handleSelect}>
          {criteria.map((c) => {
            const isDisabled = disabledCriteria.includes(c)
            const isSelected = c === selectedCriteria

            return (
              <VpRadioGroupItem
                key={c}
                aria-disabled={isDisabled}
                disabled={isDisabled}
                value={c}
                defaultChecked={isSelected}
                className={[
                  'sort-dropdown__option',
                  isSelected ? 'sort-dropdown__option--selected' : '',
                  isDisabled ? 'sort-dropdown__option--disabled' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                labelSlot={(
                  <>
                    {/* The input is named via aria-labelledby on this span; hiding it avoids a second VoiceOver read. */}
                    <VpRadioGroupItemLabel aria-hidden="true">
                      {t(`products-page.sort.criteria.${c}`)}
                    </VpRadioGroupItemLabel>
                    {isDisabled && <InfoTooltip reason="models" />}
                  </>
                )}
              />
            )
          })}
        </VpRadioGroup>
      )}
    </div>
  )
}
